/**
 * MasterDataProcessingTypeFormDialog — Create/Edit `processing_type`.
 * 1:1 port of Access `frmMdQryProcessingTypeAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=3024), field order by Top:
 *   PO Type Index (editable số) → PO Type ID (readonly) → PO Type → Description.
 * Access: po_type_id (PK) = "POTYPE" & Format(po_type_index,"00"). New:
 * Suggest_IndexName("processing_typeindex") → index + id. Edit: index disable.
 * VBA errCheck: index trống → "Please enter PO Type Index!"; id trùng (create) →
 * "PO Type ID is existed!". po_type_index KHÔNG lưu; payload gửi po_type_id (PK).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdProcessingTypeMutation,
  useLazyCheckMdPoTypeIdQuery,
  useLazySuggestMdProcessingTypeQuery,
  useUpdateMdProcessingTypeMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataProcessingType } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataProcessingType | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  po_type_index: string
  po_type_id: string
  po_type: string
  description: string
}

const EMPTY: FormState = { po_type_index: '', po_type_id: '', po_type: '', description: '' }

// Access: po_type_id = "POTYPE" & Format(index,"00").
const idFromIndex = (idx: string): string => {
  const t = idx.trim()
  if (t === '' || !/^\d+$/.test(t)) return ''
  return `POTYPE${t.padStart(2, '0')}`
}

export default function MasterDataProcessingTypeFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createPt, { isLoading: creating }] = useCreateMdProcessingTypeMutation()
  const [updatePt, { isLoading: updating }] = useUpdateMdProcessingTypeMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdProcessingTypeQuery()
  const [triggerCheck] = useLazyCheckMdPoTypeIdQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      const id = initialValue.po_type_id ?? ''
      setForm({
        po_type_index: id.replace(/^POTYPE/i, ''),
        po_type_id: id,
        po_type: initialValue.po_type ?? '',
        description: initialValue.description ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const idx = String(res.index ?? '').trim()
      if (!idx) return
      setForm((prev) => (prev.po_type_index.trim() ? prev : {
        ...prev,
        po_type_index: idx,
        po_type_id: res.po_type_id || idFromIndex(idx),
      }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  useEffect(() => {
    if (regenNotice && form.po_type_id !== regenNotice.to) setRegenNotice(null)
  }, [form.po_type_id, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const handleIndexChange = (v: string) => {
    setForm((p) => ({ ...p, po_type_index: v, po_type_id: idFromIndex(v) }))
  }

  const indexEmpty = !form.po_type_index.trim()
  const idEmpty = !form.po_type_id.trim()
  const canSave = !indexEmpty && !idEmpty && !loading

  const handleIndexBlur = async () => {
    if (isEdit) return
    const id = form.po_type_id.trim()
    if (!id) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(id, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const idx = String(next.index ?? '').trim()
      const newId = next.po_type_id || idFromIndex(idx)
      if (newId && newId !== id) {
        setForm((prev) => ({ ...prev, po_type_index: idx, po_type_id: newId }))
        setRegenNotice({ from: id, to: newId })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const buildBody = () => ({
    po_type: form.po_type.trim() || null,
    description: form.description.trim() || null,
  })

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (indexEmpty || idEmpty) {
      setShowErrors(true)
      return
    }
    try {
      if (isEdit && initialValue) {
        await updatePt({ code: initialValue.po_type_id, body: buildBody() }).unwrap()
        snack.success('Processing type updated')
      } else {
        await createPt({ po_type_id: form.po_type_id.trim(), ...buildBody() }).unwrap()
        snack.success('Processing type created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const idx = String(next.index ?? '').trim()
          const newId = next.po_type_id || idFromIndex(idx)
          const oldId = form.po_type_id.trim()
          if (newId && newId !== oldId) {
            setForm((prev) => ({ ...prev, po_type_index: idx, po_type_id: newId }))
            setRegenNotice({ from: oldId, to: newId })
            return
          }
        } catch {
          /* fallthrough */
        }
      }
      snack.error(msg)
    }
  }

  const indexHelper = isEdit
    ? 'PO Type ID cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying ID…'
      : suggestState.isFetching
        ? 'Suggesting next index…'
        : 'PO Type ID auto-derives as POTYPE + index.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Processing Type' : 'New Processing Type'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.po_type_id}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button type="button" variant="contained" size="small" color="success" onClick={() => void handleSave()} disabled={!canSave} sx={dialogHeaderBtnSx}>
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            PO Type ID <b>{regenNotice.from}</b> was just created — suggested new ID <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="PO Type Index"
              size="small"
              fullWidth
              required
              type="number"
              disabled={isEdit}
              error={showErrors && indexEmpty}
              value={form.po_type_index}
              onChange={(e) => handleIndexChange(e.target.value)}
              onBlur={() => void handleIndexBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1, inputMode: 'numeric', step: 1 }}
              helperText={indexHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="PO Type ID"
              size="small"
              fullWidth
              value={form.po_type_id}
              error={showErrors && idEmpty}
              InputLabelProps={{ shrink: true }}
              InputProps={{ readOnly: true }}
              inputProps={{ tabIndex: -1 }}
              sx={roSx}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="PO Type"
              size="small"
              fullWidth
              value={form.po_type}
              onChange={(e) => set('po_type', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
